// https://vuex.vuejs.org/en/actions.html
import axios from 'axios'
import { TokenService } from '../services/storage.service'

var apiRoot = null
var configPromise = null

function validateApiRoot (configuredRoot) {
  if (typeof configuredRoot !== 'string' || !configuredRoot.trim()) {
    throw new Error('TastingIQ API configuration is missing.')
  }

  const parsed = new URL(configuredRoot, window.location.origin)
  if (['http:', 'https:'].indexOf(parsed.protocol) === -1) {
    throw new Error('TastingIQ API configuration is invalid.')
  }

  const localHosts = ['localhost', '127.0.0.1', '::1']
  const dashboardIsLocal = localHosts.indexOf(window.location.hostname) !== -1
  const apiIsLocal = localHosts.indexOf(parsed.hostname) !== -1
  if (!dashboardIsLocal && apiIsLocal) {
    throw new Error('TastingIQ API configuration points to a local development server.')
  }

  const expectedApiHosts = {
    'client.hspbrands.com': 'portal.hspbrands.com',
    'retail.hspbrands.com': 'portal.hspbrands.com',
    'staging-client.hspbrands.com': 'staging-portal.hspbrands.com',
    'staging-retail.hspbrands.com': 'staging-portal.hspbrands.com'
  }
  const expectedApiHost = expectedApiHosts[window.location.hostname]
  if (expectedApiHost && (parsed.protocol !== 'https:' || parsed.hostname !== expectedApiHost)) {
    throw new Error('TastingIQ API configuration does not match this environment.')
  }

  return parsed.toString().replace(/\/?$/, '/')
}

export default {
  //
  get_config (context) {
    if (apiRoot !== null) return Promise.resolve(apiRoot)
    if (configPromise !== null) return configPromise

    configPromise = axios.get('/config/config.json')
      .then(function (config) {
        apiRoot = validateApiRoot(config.data && config.data.api_root)
        context.commit('setApiRoot', apiRoot)
        context.commit('setConfigError', null)
        return apiRoot
      })
      .catch(function (error) {
        context.commit('setConfigError', 'TastingIQ could not load its API configuration. Please refresh the page or contact HSP support.')
        throw error
      })
      .then(function (result) {
        configPromise = null
        return result
      }, function (error) {
        configPromise = null
        throw error
      })

    return configPromise
  },

  async set_client (context, credentials) {
    const accessKey = credentials.access_key
    const clientCode = credentials.client_code
    context.commit('clientLoad')
    try {
      await context.dispatch('get_config')
      const response = await axios.get(apiRoot + 'clients/' + clientCode + '?access_key=' + accessKey)

      if (window.DD_RUM && typeof window.DD_RUM.setUser === 'function') {
        window.DD_RUM.setUser({
          id: response.data.client_id,
          name: response.data.client_name,
          env: process.env.NODE_ENV
        })
      }

      const agencyView = response.data.agency_view ? response.data.agency_view : '0'
      const brandView = response.data.brand_view ? response.data.brand_view : '0'
      const distributorView = response.data.distributor ? response.data.distributor : '0'

      context.commit('setClient', response.data)
      context.commit('setAgencyView', agencyView)
      context.commit('setBrandView', brandView)
      context.commit('setDistributorView', distributorView)
      TokenService.saveAccessKey(accessKey)
      TokenService.saveLoginCode(clientCode)
      TokenService.saveAgencyView(agencyView)
      TokenService.saveBrandView(brandView)
      TokenService.saveDistributorView(distributorView)

      context.commit('setAccessKey', accessKey)
      context.dispatch('get_states')
      context.dispatch('get_workOrders')
      return response.data
    } catch (error) {
      TokenService.removeAccessKey()
      TokenService.removeLoginCode()
      context.commit('clientLoadError', {errorCode: error.errorCode, errorMessage: error.message})
      throw error
    }
  },

  async set_retail (context, credentials) {
    const retailKey = credentials.retail_key
    const retail = credentials.retail
    context.commit('retailLoad')
    try {
      await context.dispatch('get_config')
      const response = await axios.get(apiRoot + 'retail/' + retail + '?access_key=' + retailKey)
      const itemListView = response.data.show_item_list ? response.data.show_item_list : '0'

      context.commit('setRetail', response.data)
      TokenService.saveAccessKey(retailKey)
      TokenService.saveLoginCode(retail)
      TokenService.saveItemListView(itemListView)
      context.commit('setAccessKey', retailKey)
      context.dispatch('get_states')
      return response.data
    } catch (error) {
      TokenService.removeAccessKey()
      TokenService.removeLoginCode()
      context.commit('retailLoadError', {errorCode: error.errorCode, errorMessage: error.message})
      throw error
    }
  },

  get_client (context) {
    if (!context.state.authenticating) {
      return this.dispatch('set_client', {access_key: TokenService.getAccessKey(), client_code: TokenService.getLoginCode()})
    }
  },

  get_states (context, params = {}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        var call
        if (TokenService.getLoginType() == 'client') {
          const startDate = params.dateStart || context.state.dateFrom
          const endDate = params.dateEnd || context.state.dateTo
          call = axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/active_states?access_key=' + TokenService.getAccessKey() + '&start_date=' + startDate + '&end_date=' + endDate)
        } else {
          call = axios.get(apiRoot + 'retail/' + TokenService.getLoginCode() + '/active_states?access_key=' + TokenService.getAccessKey())
        }
        call.then(function (response) {
          response.data.unshift('All')
          context.commit('setStates', response.data)
          resolve()
        })
          .catch(function (error) {
            console.log(error)
            reject(error)
          })
      })
    })
  },

  async get_agencies (context) {
    await this.dispatch('get_config')

    if (TokenService.getLoginType() == 'client') {
      try {
        let response = await axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/active_agencies?access_key=' + TokenService.getAccessKey() + '&start_date=' + context.state.dateFrom + '&end_date=' + context.state.dateTo)

        response.data.unshift({'name': 'All'})
        context.commit('setAgenciesList', response.data)
        return response.data
      } catch (error) {
        console.error('Error in get_agencies action:', error)
        context.commit('setAgenciesList', [])
        return []
      }
    }
  },

  async get_distributors (context) {
    await this.dispatch('get_config')

    if (TokenService.getLoginType() == 'client') {
      try {
        let response = await axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/distributors?access_key=' + TokenService.getAccessKey())
        response.data.unshift({'name': 'All'})
        context.commit('setDistributorsList', response.data)
        return response.data
      } catch (error) {
        console.error('Error in get_distributors action:', error)
        context.commit('setDistributorsList', [])
        return []
      }
    }
  },

  async get_brands (context) {
    await this.dispatch('get_config')

    if (TokenService.getLoginType() == 'client') {
      try {
        let response = await axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/brands?access_key=' + TokenService.getAccessKey())
        const list = Array.isArray(response.data) ? response.data : []
        const normalized = list.map(b => (typeof b === 'string' ? { name: b } : b))
        normalized.unshift({ name: 'All' })
        context.commit('setBrandsList', normalized)
        return normalized
      } catch (error) {
        console.error('Error in get_brands action:', error)
        context.commit('setBrandsList', [{ name: 'All' }])
        return [{ name: 'All' }]
      }
    }
  },

  get_workOrders (context, params = {}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        var call
        if (TokenService.getLoginType() == 'client') {
          const startDate = params.dateStart || context.state.dateFrom
          const endDate = params.dateEnd || context.state.dateTo
          call = axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/work_orders?access_key=' + TokenService.getAccessKey() + '&start_date=' + startDate + '&end_date=' + endDate)
        } else {
          context.commit('setWorkOrders', [{ displayText: 'All', value: 'All', work_order: 'All' }])
          resolve()
          return
        }
        call.then(function (response) {
          let workOrders = response.data.map(wo => {
            let displayText = 'N/A'
            if (wo.wines && wo.wines.length > 0) {
              const firstWine = wo.wines[0]
              const date = new Date(wo.start_date)
              const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
              const woNumber = String(wo.work_order).slice(-4)
              displayText = `${woNumber} | ${firstWine.producer_name} | ${formattedDate}`
            }
            return {
              ...wo,
              displayText: displayText,
              value: wo.work_order
            }
          })
          workOrders.unshift({ displayText: 'All', value: 'All', work_order: 'All' })
          context.commit('setWorkOrders', workOrders)
          resolve()
        })
          .catch(function (error) {
            console.log(error)
            context.commit('setWorkOrders', [{ displayText: 'All', value: 'All', work_order: 'All' }])
            reject(error)
          })
      })
    })
  },

  get_storeTypes (context, params = {}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        var call
        if (TokenService.getLoginType() == 'client') {
          const startDate = params.dateStart || context.state.dateFrom || context.state.scheduleDateFrom
          const endDate = params.dateEnd || context.state.dateTo || context.state.scheduleDateTo
          call = axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/store_types?access_key=' + TokenService.getAccessKey() + '&start_date=' + startDate + '&end_date=' + endDate)
          call.then(function (response) {
            response.data.unshift('All')
            context.commit('setStoreTypes', response.data)
            resolve()
          })
            .catch(function (error) {
              console.log(error)
              reject(error)
            })
        } else {
          resolve()
        }
      })
    })
  },

  get_company (context) {
    if (!context.state.authenticating && context.state.client.client_name == '' && context.state.retail.retail_name == '') {
      if (TokenService.getLoginType() == 'retail') {
        return this.dispatch('set_retail', {retail_key: TokenService.getAccessKey(), retail: TokenService.getLoginCode()})
      }

      if (TokenService.getLoginType() == 'client') {
        return this.dispatch('set_client', {access_key: TokenService.getAccessKey(), client_code: TokenService.getLoginCode()})
      }
    }
  },

  get_recaps (context, { dateStart, dateEnd, state, storeType, agency, distributor, brand }) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        const params = new URLSearchParams({
          access_key: TokenService.getAccessKey(),
          date_start: dateStart,
          date_end: dateEnd
        })

        if (state && state !== 'All') params.append('state', state)
        if (storeType && storeType !== 'All') params.append('store_type', storeType)
        if (agency && agency !== 'All') params.append('agency', agency)
        if (distributor && distributor !== 'All') params.append('distributor', distributor)
        if (brand && brand !== 'All') params.append('brand', brand)

        let call

        if (TokenService.getLoginType() === 'client') {
          call = axios.get(
            `${apiRoot}clients/${TokenService.getLoginCode()}/recaps?${params.toString()}`
          )
        } else {
          call = axios.get(
            `${apiRoot}retail/${context.state.retail.retail_code}/recaps?${params.toString()}`
          )
        }

        call.then(function (response) {
          context.commit('setRecaps', response.data)
          resolve()
        }).catch(function (error) {
          console.log(error)
          reject(error)
        })
      })
    })
  },

  get_stats (context, {dateStart, dateEnd, state, workOrder, agency, distributor, brand, storeType}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        const params = new URLSearchParams({
          access_key: TokenService.getAccessKey(),
          date_start: dateStart,
          date_end: dateEnd
        })

        if (state && state !== 'All') params.append('state', state)
        if (workOrder && workOrder !== 'All') params.append('work_order', workOrder)
        if (agency && agency !== 'All') params.append('agency', agency)
        if (distributor && distributor !== 'All') params.append('distributor', distributor)
        if (brand && brand !== 'All') params.append('brand', brand)
        if (storeType && storeType !== 'All') params.append('store_type', storeType)

        let call
        if (TokenService.getLoginType() == 'client') {
          call = axios.get(`${apiRoot}clients/${TokenService.getLoginCode()}/stats?${params.toString()}`)
        } else {
          call = axios.get(`${apiRoot}retail/${context.state.retail.retail_code}/stats?${params.toString()}`)
        }
        call.then(function (response) {
          context.commit('setStats', response.data)
          resolve()
        }).catch(function (error) {
          console.log(error)
          reject()
        })
      })
    })
  },

  async get_topAgencies (context, { dateStart, dateEnd, state, workOrder}) {
    await this.dispatch('get_config')
    
    var state_param = ''
    if (state != 'All') state_param = '&state=' + state
    var workOrder_param = ''
    if (workOrder != 'All' && workOrder != '') workOrder_param = '&work_order=' + workOrder
    
    if (TokenService.getLoginType() == 'client') {
      try {
        let response
        response = await axios.get(apiRoot + 'clients/' + TokenService.getLoginCode() + '/agencies?access_key=' + TokenService.getAccessKey() + '&date_start=' + dateStart + '&date_end=' + dateEnd + state_param + workOrder_param)

        context.commit('setTopAgencies', response.data)
        return response.data
      } catch (error) {
        console.error('Error in get_topAgencies action:', error)
        context.commit('setTopAgencies', [])
        return []
      }
    } else {
      console.warn('get_topAgencies called when loginType is not "client"')
      context.commit('setTopAgencies', [])
      return []
    }
  },
  
  // NEW ASYNC/AWAIT VERSION (EXTREMELY COOL, PEOPLE LOVE IT, INTERNATIONAL RECOGNITION)
  async get_schedule (context, { dateStart, dateEnd, state, workOrder, distributor, brand, store}) {
      await this.dispatch('get_config')

      const params = new URLSearchParams({
        access_key: TokenService.getAccessKey(),
        date_start: dateStart,
        date_end: dateEnd
      })

      if (state && state !== 'All') params.append('state', state)
      if (workOrder && workOrder !== 'All' && workOrder !== '') params.append('work_order', workOrder)
      if (distributor && distributor !== 'All' && distributor !== '') params.append('distributor', distributor)
      if (brand && brand !== 'All' && brand !== '') params.append('brand', brand)
      if (store && store !== 'All' && store !== '') params.append('store', store)
      
      var call
      
      if (TokenService.getLoginType() == 'client') {
        call = await axios.get(
          `${apiRoot}clients/${TokenService.getLoginCode()}/schedule?${params.toString()}`
        )
      } else {
        call = await axios.get(
          `${apiRoot}retail/${TokenService.getLoginCode()}/schedule?${params.toString()}`
        )
      }

      let arr = call.data
      const filteredArr = arr.filter(obj => obj.outcome === null)

      context.commit('setSchedule', filteredArr)
      return filteredArr
    },

  get_sales (context, {dateStart, dateEnd, state, workOrder, agency, distributor, brand, storeType}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        const params = new URLSearchParams({
          access_key: TokenService.getAccessKey(),
          date_start: dateStart,
          date_end: dateEnd
        })

        if (state && state !== 'All') params.append('state', state)
        if (workOrder && workOrder !== 'All') params.append('work_order', workOrder)
        if (agency && agency !== 'All') params.append('agency', agency)
        if (distributor && distributor !== 'All') params.append('distributor', distributor)
        if (brand && brand !== 'All') params.append('brand', brand)
        if (storeType && storeType !== 'All') params.append('store_type', storeType)

        axios.get(`${apiRoot}clients/${TokenService.getLoginCode()}/sales?${params.toString()}`)
          .then(function (response) {
            context.commit('setSales', response.data)
            resolve()
          }).catch(function (error) {
            console.log(error)
            reject()
          })
      })
    })
  },

  get_storeSales (context, {dateStart, dateEnd, state, workOrder, agency, distributor, brand, storeType}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        const params = new URLSearchParams({
          access_key: TokenService.getAccessKey(),
          date_start: dateStart,
          date_end: dateEnd
        })

        if (state && state !== 'All') params.append('state', state)
        if (workOrder && workOrder !== 'All') params.append('work_order', workOrder)
        if (agency && agency !== 'All') params.append('agency', agency)
        if (distributor && distributor !== 'All') params.append('distributor', distributor)
        if (brand && brand !== 'All') params.append('brand', brand)
        if (storeType && storeType !== 'All') params.append('store_type', storeType)

        axios.get(`${apiRoot}clients/${TokenService.getLoginCode()}/stores?${params.toString()}`)
          .then(function (response) {
            context.commit('setStoreSales', response.data)
            resolve()
          }).catch(function (error) {
            console.log(error)
            reject()
          })
      })
    })
  },

  get_supplierSales (context, {dateStart, dateEnd, state}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        var state_param = ''
        if (state != 'All') state_param = '&state=' + state

        axios.get(apiRoot + 'retail/' + context.state.retail.retail_code + '/suppliers?access_key=' + TokenService.getAccessKey() + '&date_start=' + dateStart + '&date_end=' + dateEnd + state_param)
          .then(function (response) {
            context.commit('setSupplierSales', response.data)
            resolve()
          }).catch(function (error) {
            console.log(error)
            reject()
          })
      })
    })
  },

  get_retailStoreSales (context, {dateStart, dateEnd, state}) {
    return new Promise((resolve, reject) => {
      this.dispatch('get_config').then(function () {
        var state_param = ''
        if (state != 'All') state_param = '&state=' + state

        axios.get(apiRoot + 'retail/' + context.state.retail.retail_code + '/stores?access_key=' + TokenService.getAccessKey() + '&date_start=' + dateStart + '&date_end=' + dateEnd + state_param)
          .then(function (response) {
            context.commit('setRetailStoreSales', response.data)
            resolve()
          }).catch(function (error) {
            console.log(error)
            reject()
          })
      })
    })
  },

  async login (context, { username, password }) {
    // Get the apiRoot from the config file
    await this.dispatch('get_config')
    try {
      // Headers included to fulfill requirement for CORS "simple request"
      const response = await axios.post(apiRoot + 'login', {
        username: username,
        password: password
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (response.data.status === 'failure') {
        throw new Error(response.data.message || 'Login failed')
      } else {
        return response.data
      }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  },

  async forgotPassword (context, { email }) {
    await context.dispatch('get_config')
    const response = await axios.post(apiRoot + 'auth/forgot-password', {
      email: email
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return response.data
  },

  async updatePassword (context, { username, newPassword, oldPassword }) {
    // Get the apiRoot from the config file
    await this.dispatch('get_config')
    try {
      const response = await axios.post(apiRoot + 'change_password', {
        username: username,
        newPassword: newPassword,
        oldPassword: oldPassword
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      return response.data

    } catch (error) {
      console.error('Update password error:', error)
      throw error
    }
  },

  async fetchSalesTrends (context, { startDate, endDate }) {
    await this.dispatch('get_config')
    
    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()
    
    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: startDate,
      end_date: endDate
    })
    
    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/sales_trends?${params}`)
      }
      
      return response.data
    } catch (error) {
      console.error('Error fetching sales data:', error)
      throw error
    }
  },

  async fetchAgencyBarData (context, { startDate, endDate }) {
    await this.dispatch('get_config')
    
    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()
    
    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: startDate,
      end_date: endDate
    })
    
    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/agency_bar?${params}`)
      }
      
      return response.data
    } catch (error) {
      console.error('Error fetching agency bar sales data:', error)
      throw error
    }
  },

  async fetchCumulativeSamples (context, { startDate, endDate }) {
    await this.dispatch('get_config')
    
    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()
    
    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: startDate,
      end_date: endDate
    })
    
    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/cumulative_samples?${params}`)
      }
      
      return response.data
    } catch (error) {
      console.error('Error fetching agency bar sales data:', error)
      throw error
    }
  },

  async fetchEventPerformance (context, { startDate, endDate }) {
    await this.dispatch('get_config')
    
    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()
    
    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: startDate,
      end_date: endDate
    })
    
    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/event_performance?${params}`)
      }
      
      return response.data
    } catch (error) {
      console.error('Error fetching event performance data:', error)
      throw error
    }
  },

  async fetchYearOverYearData (context, { startDate, endDate }) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    const params = new URLSearchParams({
      access_key: accessKey
    })

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/year_over_year?${params}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching year-over-year data:', error)
      throw error
    }
  },

  async fetchProductSalesData (context, { startDate, endDate }) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    const params = new URLSearchParams({
      access_key: accessKey
    })

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/product_sales_pie?${params}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching product sales pie data:', error)
      throw error
    }
  },

  async fetchProductUnitsData (context, { startDate, endDate }) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    const params = new URLSearchParams({
      access_key: accessKey
    })

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/product_units_bar?${params}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching product units pie data:', error)
      throw error
    }
  },

  async fetchRetailerSalesAvg (context, { startDate, endDate }) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode() 
    const accessKey = TokenService.getAccessKey()

    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: startDate,
      end_date: endDate
    })

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/retail_sales_avg?${params}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching retailer sales average data:', error)
      throw error
    }
  },

  async fetchSipBoostImages (context) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/sip_boost_images?access_key=${accessKey}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching SIP Boost images:', error)
      throw error
    }
  },

  async fetchSipReviews (context) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    try {
      let response
      if (loginType === 'client') {
        response = await axios.get(`${apiRoot}clients/${loginCode}/sip_review_images?access_key=${accessKey}`)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching SIP reviews:', error)
      throw error
    }
  },

  async generateShareToken (context, { dateStart, dateEnd, state, workOrder, brand, distributor, store }) {
    await this.dispatch('get_config')

    const loginType = TokenService.getLoginType()
    const loginCode = TokenService.getLoginCode()
    const accessKey = TokenService.getAccessKey()

    const params = new URLSearchParams({
      access_key: accessKey,
      start_date: dateStart,
      end_date: dateEnd
    })

    if (state && state !== 'All') {
      params.append('state', state)
    }
    if (workOrder && workOrder !== 'All') {
      params.append('work_order', workOrder)
    }
    if (brand && brand !== 'All') {
      params.append('brand', brand)
    }
    if (distributor && distributor !== 'All') {
      params.append('distributor', distributor)
    }
    if (store && store !== 'All') {
      params.append('store', store)
    }

    try {
      let response
      if (loginType === 'client') {
        response = await axios.post(`${apiRoot}clients/${loginCode}/generate_token`, params, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
      }
      if (loginType === 'retail') {
        response = await axios.post(`${apiRoot}retail/${loginCode}/generate_token`, params, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
      }
      return response.data
    } catch (error) {
      console.error('Error generating share token:', error)
      throw error
    }
  }

}
